'use client'

import { CheckIcon, CopyIcon, Loader2Icon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { SettingsCard, SettingsCardHeader } from '@/components/dialog/settings/settings-card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  type CreditLedgerRow,
  runningBalances,
} from '@/features/settings/billing/credits/lib/credits'
import { useCreditFormat } from '@/features/settings/billing/credits/use-credit-format'
import { copyText } from '@/lib/clipboard'
import { cn } from '@/lib/utils'
import { useCreditLedger, useResetCreditLedger } from '@/service/api-billing.hook'
import { useListApiKeys } from '@/service/api-key.hook'
import { CreditLedgerKind } from '@/types/generated/public_api'

const ALL_KEYS = '__all__'

// Dense ledger cells: the settings dialog's panel is ~490px wide inside a card, and the
// table's stock p-4 / h-12 left room for barely three columns. The outer columns sit flush
// with the card's own padding.
const HEAD = 'h-8 px-3 text-xs font-normal first:pl-0 last:pr-0'
const CELL = 'px-3 py-2.5 text-xs first:pl-0 last:pr-0'
// Every column but Reference shrinks to its content; Reference takes what's left and
// truncates (`max-w-0` is what lets a `w-full` cell shrink below its text in an auto table).
const FIT = 'w-px whitespace-nowrap'

const KIND_LABEL = {
  [CreditLedgerKind.CREDIT_LEDGER_KIND_SPEND]: 'SPEND',
  [CreditLedgerKind.CREDIT_LEDGER_KIND_REFUND]: 'REFUND',
  [CreditLedgerKind.CREDIT_LEDGER_KIND_PURCHASE]: 'PURCHASE',
  [CreditLedgerKind.CREDIT_LEDGER_KIND_AUTO_REFILL]: 'AUTO_REFILL',
  [CreditLedgerKind.CREDIT_LEDGER_KIND_ADMIN_GRANT]: 'ADMIN_GRANT',
  [CreditLedgerKind.CREDIT_LEDGER_KIND_OPENING]: 'OPENING',
  [CreditLedgerKind.CREDIT_LEDGER_KIND_CLAWBACK]: 'CLAWBACK',
} as const

function kindLabel(kind: CreditLedgerKind) {
  return KIND_LABEL[kind as keyof typeof KIND_LABEL] ?? 'UNSPECIFIED'
}

// The reference fills its column and truncates, so every row's copy button lines up at the
// column's right edge instead of trailing each value at a different x.
function CopyReference({ value }: { value: string }) {
  const t = useTranslations('settings.billing.credits.history')
  const [copied, setCopied] = useState(false)
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className="text-foreground/70 min-w-0 flex-1 truncate font-mono text-xs" title={value}>
        {value}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="iconXs"
        aria-label={t('copy')}
        className="text-foreground/50 hover:text-foreground shrink-0"
        onClick={async () => setCopied(await copyText(value))}
      >
        {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
      </Button>
    </span>
  )
}

/**
 * The credit ledger, newest first. The running balance is only drawn unfiltered: it's worked
 * backwards from today's balance, and a key filter leaves purchases and grants out of the sum.
 */
export default function CreditHistoryCard({ balance }: { balance?: number }) {
  const t = useTranslations('settings.billing.credits.history')
  const locale = useLocale()
  const { credits } = useCreditFormat()
  const [keyFilter, setKeyFilter] = useState(ALL_KEYS)
  const keyId = keyFilter === ALL_KEYS ? undefined : keyFilter
  const ledger = useCreditLedger(keyId)
  const resetLedger = useResetCreditLedger()
  const { data: apiKeys } = useListApiKeys()

  const entries = useMemo<CreditLedgerRow[]>(
    () => ledger.data?.pages.flatMap((page) => page.entries) ?? [],
    [ledger.data]
  )
  const balances = useMemo(
    () => (keyId === undefined && balance !== undefined ? runningBalances(entries, balance) : null),
    [balance, entries, keyId]
  )

  // This year's entries drop the year ("Sep 30, 4:13 PM"); older ones keep it.
  const thisYear = new Date().getFullYear()
  const dateTime = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  const dateTimeWithYear = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  const formatWhen = (ms: number) =>
    (new Date(ms).getFullYear() === thisYear ? dateTime : dateTimeWithYear).format(ms)

  const loadMore = (
    <Button
      variant="secondary"
      size="sm"
      className="self-center"
      disabled={ledger.isFetchingNextPage}
      onClick={() => void ledger.fetchNextPage()}
    >
      {ledger.isFetchingNextPage ? (
        <Loader2Icon className="size-3.5 animate-spin" />
      ) : (
        t('loadMore')
      )}
    </Button>
  )

  const retry = (className?: string) => (
    <div className={cn('flex items-center gap-3', className)}>
      <p className="text-foreground/60 text-sm">{t('error')}</p>
      <Button variant="secondary" size="sm" onClick={() => resetLedger(keyId)}>
        {t('retry')}
      </Button>
    </div>
  )

  return (
    <SettingsCard className="gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SettingsCardHeader title={t('title')} />
        <Select value={keyFilter} onValueChange={setKeyFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_KEYS}>{t('allKeys')}</SelectItem>
            {(apiKeys?.keys ?? []).map((key) => (
              <SelectItem key={key.keyId} value={key.keyId}>
                {key.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {ledger.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : ledger.isError && entries.length === 0 ? (
        retry('py-4')
      ) : entries.length === 0 ? (
        // A key filter can run out of hops before finding a row: older history may still exist.
        ledger.hasNextPage ? (
          loadMore
        ) : (
          <p className="text-foreground/60 py-4 text-sm">{t('empty')}</p>
        )
      ) : (
        <>
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-foreground/10 border-b">
                  <TableHead className={cn(HEAD, FIT)}>{t('cols.date')}</TableHead>
                  <TableHead className={cn(HEAD, FIT)}>{t('cols.kind')}</TableHead>
                  <TableHead className={cn(HEAD, FIT, 'text-right')}>{t('cols.credits')}</TableHead>
                  <TableHead className={cn(HEAD, 'w-full')}>{t('cols.reference')}</TableHead>
                  {balances && (
                    <TableHead className={cn(HEAD, FIT, 'text-right')}>
                      {t('cols.balance')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, index) => {
                  const isSpend = entry.kind === CreditLedgerKind.CREDIT_LEDGER_KIND_SPEND
                  const isRefund = entry.kind === CreditLedgerKind.CREDIT_LEDGER_KIND_REFUND
                  return (
                    <TableRow
                      key={`${entry.createdAtMs}-${entry.reference}-${index}`}
                      className="border-foreground/5 border-b last:border-b-0"
                    >
                      <TableCell className={cn(CELL, FIT, 'tabular-nums')}>
                        {/* A spend entry is a whole UTC day, stamped at its midnight. */}
                        {isSpend
                          ? t('utcDay', { day: entry.reference })
                          : formatWhen(entry.createdAtMs)}
                      </TableCell>
                      <TableCell className={cn(CELL, FIT)}>
                        {t(`kinds.${kindLabel(entry.kind)}`)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          CELL,
                          FIT,
                          'text-right tabular-nums',
                          entry.credits > 0 && 'text-chart-mint',
                          entry.credits < 0 && 'text-chart-red'
                        )}
                      >
                        {credits(entry.credits, { signDisplay: 'exceptZero' })}
                      </TableCell>
                      <TableCell className={cn(CELL, 'w-full max-w-0')}>
                        {!isSpend && entry.reference && (
                          <div className="flex flex-col gap-0.5">
                            <CopyReference value={entry.reference} />
                            {isRefund && (entry.source || entry.dataset) && (
                              <span className="text-foreground/50 truncate text-xs">
                                {[entry.source, entry.dataset].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      {balances && (
                        <TableCell className={cn(CELL, FIT, 'text-right tabular-nums')}>
                          {credits(balances[index])}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          {ledger.isError && retry()}
          {ledger.hasNextPage && !ledger.isError && loadMore}
        </>
      )}
    </SettingsCard>
  )
}
